import { createContext, useContext, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import getRegistrationDetailsService from "@/services/NPS/Stepper/APIs/getRegistrationDetailsService";
import { Registration } from "@/utils/types/NPS/Stepper/GetRegistrationDetils";
import { getAccessToken } from "@/lib/storeManager";

interface RegistrationDetailsContextType {
  registrationDetails: Registration | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

const RegistrationDetailsContext = createContext<
  RegistrationDetailsContextType | undefined
>(undefined);

interface RegistrationDetailsProviderProps {
  children: ReactNode;
}

export const RegistrationDetailsProvider: React.FC<
  RegistrationDetailsProviderProps
> = ({ children }) => {
  const token = getAccessToken();
  const isLoggedIn = !!token;

  const {
    data: registrationDetailsData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["npsData"],
    queryFn: () => getRegistrationDetailsService.getRegistrationDetails(),
    enabled: isLoggedIn, // Only fetch when user is logged in
  });

  const registrationDetails = registrationDetailsData?.registration ?? null;

  return (
    <RegistrationDetailsContext.Provider
      value={{
        registrationDetails,
        isLoading,
        isError,
        error: error as Error | null,
        refetch,
      }}
    >
      {children}
    </RegistrationDetailsContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useRegistrationDetails = () => {
  const context = useContext(RegistrationDetailsContext);
  if (!context) {
    throw new Error(
      "useRegistrationDetails must be used within a RegistrationDetailsProvider"
    );
  }
  return context;
};
